import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {useUpdateCreditsCache} from "@/api/companyApi";
import InfoIcon from "@/assets/colorable/info.svg?react";
import Modal from "@/components/Common/Modal";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import useCampaignInfluencerFilterStore from "@/store/campaignInfluencerFilter";
import useCampaignManagementStore from "@/store/campaignManagement";
import {refundInfluencerModalDataAtom} from "@/store/jotaiAtoms";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const RefundInfluencerModal: FC = () => {
  const {t} = useTranslation("common");
  const queryClient = useQueryClient();
  const formatPrice = usePriceFormat();
  const campaign = useCampaignManagementStore(state => state.campaign);
  const dateFilter = useCampaignManagementStore(state => state.reportingDateFilter);
  const search = useCampaignInfluencerFilterStore(state => state.filters.search);
  const closeModal = () => setData({amount: 0, applicationId: null, isOpen: false});
  const [data, setData] = useAtom(refundInfluencerModalDataAtom);
  const updateCreditsCache = useUpdateCreditsCache();
  const isCreditRefund = data.payment_source === "invoice";

  const {mutate: refundCashReward, isPending} = useMutation({
    mutationKey: CAMPAIGN_APPLICATION_KEYS.mApplicationRefund(data.applicationId!),
    mutationFn: () => client.post(CAMPAIGN_APPLICATION_KEYS.mApplicationRefund(data.applicationId!)[0]),
    onSuccess: () => {
      capturePostHogEvent(PostHogEvents.ApplicationRefunded, {
        applicationId: data.applicationId,
        campaignId: campaign.id,
        isCreditRefund,
        refundAmount: data.amount,
      });

      if (isCreditRefund) {
        updateCreditsCache(data.amount);
      }

      [
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id, search),
        CAMPAIGN_KEYS.qCampaignCancelledApplications(campaign.id),
        CAMPAIGN_KEYS.qCampaignPriceSummary(campaign.id),
        CAMPAIGN_KEYS.qCampaignReportingApplications({campaignId: campaign.id, dateFilter, searchParam: search}),
      ].forEach(queryKey => queryClient.invalidateQueries({queryKey}));
      closeModal();
    },
  });

  return (
    <Modal
      backgroundClassName="max-w-104"
      confirmButtonText={t("confirm_refund")}
      isLoading={isPending}
      modalTitle={t("refund_influencer")}
      negativeButtonText={t("cancel")}
      onClose={closeModal}
      onConfirm={() => refundCashReward()}
      shouldCloseOnConfirm={false}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-neutral-black text-xl leading-5 font-semibold">
            {isCreditRefund ? t("x_credits", {count: data.amount}) : formatPrice(data.amount)}
          </span>
          <span className="text-neutral-black text-xs">{t("fees_included")}</span>
        </div>
        <div className="bg-primary-light-solid flex items-center gap-2 rounded-md p-3">
          <InfoIcon className="text-primary-sky size-3.5 shrink-0" />
          <span className="text-neutral-black text-xs">{t("refund_influencer_warning")}</span>
        </div>
      </div>
    </Modal>
  );
};

export default RefundInfluencerModal;
